import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';
import { PartyUidSchema } from './general';

export type SignUpEvents = Static<typeof SignUpEventsSchema>;
export const SignUpEventsSchema = Type.Object({
  // SIGN_UP: Added when customer signs up
  SIGN_UP: Type.Optional(
    Type.Object({
      createdAt: Type.String({ format: 'date-time' }),
      updatedAt: Type.String({ format: 'date-time' }),
      value: Type.Boolean(),
    })
  ),
  // OPT_OUT: Added when customer opts out through CAT MFE
  OPT_OUT: Type.Optional(
    Type.Object({
      createdAt: Type.String({ format: 'date-time' }),
      updatedAt: Type.String({ format: 'date-time' }),
      value: Type.Boolean(),
      addedBy: Type.String({ format: 'email' }),
    })
  ),
  // MANUALLY_ADDED: Added when customer is added manually by a coworker
  MANUALLY_ADDED: Type.Optional(
    Type.Object({
      createdAt: Type.String({ format: 'date-time' }),
      updatedAt: Type.String({ format: 'date-time' }),
      value: Type.Boolean(),
      addedBy: Type.String({ format: 'email' }),
      eventDate: Type.String({ format: 'date-time' }),
    })
  ),
});

export const SignUpEventTypesSchema = Type.Union(
  Object.keys(SignUpEventsSchema).map((key) => Type.Literal(key)),
  {
    $id: 'SignUpEventTypesSchema',
  }
);

/**
 * Database schema for sign ups
 *
 * - createdAt: timestamp when added
 * - updatedAt: timestamp when last updated
 * - value: True if user is participating, false otherwise, based on combined values of events
 * - events: Object containing all events for this sign up
 */
export type SignUp = Static<typeof SignUpSchema>;
export const SignUpSchema = Type.Object(
  {
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
    retailUnit: Type.String({ minLength: 2, maxLength: 2 }),
    lotteryId: Type.String(),
    lotteryDrawId: Type.String(),
    partyUid: PartyUidSchema,
    value: Type.Boolean(),
    events: SignUpEventsSchema,
  },
  { additionalProperties: false, $id: 'SignUpSchema' }
);

export type PartialSignUp = Omit<SignUp, 'events' | 'value' | 'updatedAt' | 'createdAt'> & {
  createdAt?: string;
};

export type CustomerSignUpEvent = { type: 'SIGN_UP'; value: boolean; timestamp: string };
export type OptOutEvent = { type: 'OPT_OUT'; value: boolean; timestamp: string; addedBy: string };
export type ManuallyAddedEvent = {
  type: 'MANUALLY_ADDED';
  value: boolean;
  timestamp: string;
  addedBy: string;
  eventDate: string;
};
export type SignUpEvent = CustomerSignUpEvent | OptOutEvent | ManuallyAddedEvent;
